import React, { useState, useMemo, useCallback } from 'react';

import {
  Modal,
  Stack,
  Title,
  Text,
  Table,
  Checkbox,
  Badge,
  Button,
  Group,
  Alert,
  ScrollArea,
  createStyles,
} from '@mantine/core';
import { IconDownload, IconAlertTriangle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import usePayrollPeriods from 'hooks/payrollPeriod/usePayrollPeriods';
import useBulkPayrollExport from 'hooks/reports/payroll/useBulkPayrollExport';
import useCoachBulkPayrollExport from 'hooks/reports/payroll/useCoachBulkPayrollExport';
import { PayrollPeriod } from 'types';
import notificationHandler from 'utils/notifications';

const useStyles = createStyles(theme => ({
  tableContainer: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
  },
  actionButtons: {
    borderTop: `1px solid ${theme.colors.gray[3]}`,
    paddingTop: theme.spacing.sm,
  },
}));

interface ModalBulkPayrollExportProps {
  opened: boolean;
  handleClose: () => void;
  // Optional coach props - when provided, modal operates in coach mode
  staffId?: string;
  staffName?: string;
}

const ModalBulkPayrollExport = ({
  opened,
  handleClose,
  staffId,
  staffName,
}: ModalBulkPayrollExportProps): JSX.Element => {
  const { classes } = useStyles();
  const { successNotification, errorNotification } = notificationHandler();

  const isCoachMode = Boolean(staffId);

  const [selectedPeriodIds, setSelectedPeriodIds] = useState<Set<string>>(new Set());
  const bulkExportMutation = useBulkPayrollExport();
  const coachBulkExportMutation = useCoachBulkPayrollExport();

  // Use the appropriate mutation based on mode
  const activeMutation = isCoachMode ? coachBulkExportMutation : bulkExportMutation;

  // Filter periods by staffId when in coach mode
  const {
    data: periodsData,
    isLoading: periodsLoading,
    isSuccess,
  } = usePayrollPeriods({
    limit: '10000',
    ...(staffId && { staff_id: staffId }),
  });

  const periods = useMemo(() => {
    if (!isSuccess || !periodsData) return [];
    return periodsData.pages
      .flatMap(page => page.data.results)
      .sort((a, b) => dayjs(b.startDate).valueOf() - dayjs(a.startDate).valueOf());
  }, [periodsData, isSuccess]);

  const handleTogglePeriod = useCallback((periodId: string) => {
    setSelectedPeriodIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(periodId)) {
        newSet.delete(periodId);
      } else {
        newSet.add(periodId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedPeriodIds(new Set(periods.map(p => p.payrollPeriodId)));
  }, [periods]);

  const handleSelectYTD = useCallback(() => {
    const currentYear = dayjs().year();
    const ytdPeriodIds = periods
      .filter(p => dayjs(p.startDate).year() === currentYear)
      .map(p => p.payrollPeriodId);
    setSelectedPeriodIds(new Set(ytdPeriodIds));
  }, [periods]);

  const handleClearSelection = useCallback(() => {
    setSelectedPeriodIds(new Set());
  }, []);

  const handleToggleAll = useCallback(() => {
    if (selectedPeriodIds.size === periods.length) {
      handleClearSelection();
    } else {
      handleSelectAll();
    }
  }, [selectedPeriodIds.size, periods.length, handleClearSelection, handleSelectAll]);

  const onClose = (): void => {
    setSelectedPeriodIds(new Set());
    handleClose();
  };

  const handleExport = async (): Promise<void> => {
    const selectedIds = Array.from(selectedPeriodIds);

    if (isCoachMode && staffId) {
      coachBulkExportMutation.mutate(
        { staffId, payrollPeriodIds: selectedIds },
        {
          onSuccess: () => {
            successNotification(
              'Export successful',
              `Exported payroll data for ${staffName || 'coach'} across ${selectedIds.length} period(s).`,
            );
            onClose();
          },
          onError: error => {
            const message =
              error.response?.data?.message ||
              'An error occurred while generating the export. Please try again.';
            errorNotification('Export failed', message);
          },
        },
      );
    } else {
      bulkExportMutation.mutate(
        { payrollPeriodIds: selectedIds },
        {
          onSuccess: () => {
            successNotification(
              'Export successful',
              `Exported payroll data for ${selectedIds.length} period(s).`,
            );
            onClose();
          },
          onError: error => {
            const message =
              error.response?.data?.message ||
              'An error occurred while generating the export. Please try again.';
            errorNotification('Export failed', message);
          },
        },
      );
    }
  };

  const formatDateRange = (period: PayrollPeriod): string => {
    return `${dayjs(period.startDate).format('MMMM D  YYYY')} - ${dayjs(period.endDate).format(
      'MMMM D YYYY',
    )}`;
  };

  const renderStatus = (period: PayrollPeriod): JSX.Element => {
    if (!period.isClosed) {
      return (
        <Badge color='kiloBlue' variant='filled' size='sm'>
          OPEN
        </Badge>
      );
    }
    return (
      <Text size='sm' color='dimmed'>
        Closed on {dayjs(period.updatedAt).format('MMM D, YYYY')}
      </Text>
    );
  };

  const allSelected = periods.length > 0 && selectedPeriodIds.size === periods.length;
  const someSelected = selectedPeriodIds.size > 0 && selectedPeriodIds.size < periods.length;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Title order={4}>
          {isCoachMode
            ? `Export Payroll Report${staffName ? ` - ${staffName}` : ''}`
            : 'Export Payroll Reports'}
        </Title>
      }
      size='lg'
      centered
    >
      <Stack spacing='md'>
        <Text size='sm' color='dimmed'>
          {isCoachMode
            ? 'Generate payroll export for selected pay periods.'
            : 'Generate summary for selected pay periods.'}
        </Text>

        <Group position='apart'>
          <Text size='sm' fw={500}>
            Select periods to include
          </Text>
          <Text size='sm' color='dimmed'>
            {selectedPeriodIds.size} Selected
          </Text>
        </Group>

        <ScrollArea style={{ height: 280 }} className={classes.tableContainer}>
          <Table highlightOnHover>
            <thead>
              <tr>
                <th style={{ width: 50 }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleToggleAll}
                    aria-label='Select all periods'
                    disabled={periodsLoading}
                  />
                </th>
                <th>Period</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {periods.map(period => (
                <tr key={period.payrollPeriodId}>
                  <td>
                    <Checkbox
                      checked={selectedPeriodIds.has(period.payrollPeriodId)}
                      onChange={() => handleTogglePeriod(period.payrollPeriodId)}
                      aria-label={`Select period ${formatDateRange(period)}`}
                    />
                  </td>
                  <td>{formatDateRange(period)}</td>
                  <td>{renderStatus(period)}</td>
                </tr>
              ))}
              {periodsLoading && (
                <tr>
                  <td colSpan={3}>
                    <Text size='sm' color='dimmed' align='center'>
                      Loading payroll periods...
                    </Text>
                  </td>
                </tr>
              )}
              {!periodsLoading && periods.length === 0 && (
                <tr>
                  <td colSpan={3}>
                    <Text size='sm' color='dimmed' align='center'>
                      No payroll periods found
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>

        <Group spacing='xs' className={classes.actionButtons}>
          <Button variant='subtle' size='xs' onClick={handleSelectAll} disabled={periodsLoading}>
            Select All
          </Button>
          <Button variant='subtle' size='xs' onClick={handleSelectYTD} disabled={periodsLoading}>
            Select All YTD
          </Button>
          <Button
            variant='subtle'
            size='xs'
            color='gray'
            onClick={handleClearSelection}
            disabled={periodsLoading}
          >
            Clear Selection
          </Button>
        </Group>

        {!isCoachMode && (
          <Alert icon={<IconAlertTriangle size={16} />} color='yellow' variant='light'>
            <Text size='sm'>
              <strong>Note:</strong> Coach-specific exports with detailed compensation breakdowns are
              available separately via the Individual Coach Report section.
            </Text>
          </Alert>
        )}

        <Group position='right' pt='md'>
          <Button variant='light' onClick={onClose} disabled={activeMutation.isLoading}>
            Cancel
          </Button>
          <Button
            leftIcon={<IconDownload size={16} />}
            onClick={handleExport}
            disabled={selectedPeriodIds.size === 0}
            loading={activeMutation.isLoading}
            data-cy='exportSelectedPeriodsButton'
          >
            Export Selected Periods
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModalBulkPayrollExport;
